import type { WithT } from 'i18next';
import type { JSX } from 'react';
import React from 'react';

import type { GrowthCreditEntry } from '@feathr/blackbox';
import { EGrowthCreditEntryClass } from '@feathr/blackbox';
import { type IColumn, TableColumnHeader, Text } from '@feathr/components';
import GrowthCreditIcon from '@feathr/extender/components/GrowthCreditIcon/GrowthCreditIcon';
import { moment, TimeFormat } from '@feathr/hooks';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';
import * as styles from './creditsRedeemedColumns.css';

export function getRedemptionTypeLabel(cls: EGrowthCreditEntryClass, t: WithT['t']): string {
  switch (cls) {
    case EGrowthCreditEntryClass.MediaRedemption:
      return t('Media Balance');

    case EGrowthCreditEntryClass.InvoiceRedemption:
      return t('Invoice Payment');

    default:
      return t('Redemption');
  }
}

export function creditsRedeemedColumns({ t }: WithT): Array<IColumn<GrowthCreditEntry>> {
  return [
    {
      id: 'date_entered',
      Header: TableColumnHeader({
        sortType: 'alpha',
        title: t('Transaction date'),
      }),
      headerClassName: tableStyles.sort,
      sortable: true,
      Cell({ original }): JSX.Element {
        return (
          <Text.DefaultMedium>
            {moment(original.get('date_entered')).format(TimeFormat.shortDate)}
          </Text.DefaultMedium>
        );
      },
    },
    {
      id: 'credits',
      Header: TableColumnHeader({
        sortType: 'numeric',
        title: t('Credits Redeemed'),
      }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: true,
      Cell({ original }): JSX.Element {
        const credits = original.get('credits');
        return (
          <div className={styles.creditsCell}>
            <Text.DefaultRegular>{Math.abs(credits).toLocaleString()}</Text.DefaultRegular>
            <GrowthCreditIcon />
          </div>
        );
      },
    },
    {
      id: '_cls',
      Header: TableColumnHeader({
        sortType: 'alpha',
        title: t('Type'),
      }),
      headerClassName: tableStyles.sort,
      sortable: true,
      Cell({ original }): JSX.Element {
        const cls = original.get('_cls');
        return <Text.DefaultRegular>{getRedemptionTypeLabel(cls, t)}</Text.DefaultRegular>;
      },
    },
    {
      id: 'description',
      Header: TableColumnHeader({
        sortType: 'alpha',
        title: t('Description'),
      }),
      headerClassName: tableStyles.sort,
      sortable: true,
      Cell({ original }): JSX.Element {
        return <Text.DefaultRegular>{original.get('description')}</Text.DefaultRegular>;
      },
    },
  ];
}
